import React, {useState, useEffect, useMemo} from "react";
import {Helper} from "../../../tools/Helper";
import {api_Routes} from "../../../tools/api_Routes";
import {useNavigate} from "react-router-dom";
import {
    errorToaster,
    successToaster,
} from "../../../helper/toasterConfiguration";
import {Styles} from "./styles";
import {
    Autocomplete,
    Button,
    CircularProgress,
    TextField,
} from "@mui/material";

const PanelAddOrderRequest = ({
                                  requests,
                                  isRequestsLoading,
                                  users,
                                  isUsersLoading,
                              }) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [note, setNote] = useState("");

    // Add state for selected user
    const [selectedUser, setSelectedUser] = useState(null);

    // Initialize data with empty strings
    const [data, setData] = useState({
        request_id: "",
        amount: "",
        user_id: "",
        note: "",
    });

    // Use useMemo to filter requests by selected user
    const userFilteredRequests = useMemo(() => {
        if (selectedUser && Array.isArray(requests)) {
            return requests.filter(request => request.user_id === selectedUser.id);
        }
        return Array.isArray(requests) ? requests : [];
    }, [selectedUser, requests]);

    // Find the current selected request for Autocomplete
    const selectedRequest = useMemo(() =>
        userFilteredRequests.find(request => request.request_id === data.request_id) || null
        , [userFilteredRequests, data.request_id]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setData((prev) => ({...prev, [name]: value}));
    };

    const handleRequestChange = (event, value) => {
        // Reset the request selection if clearing the field
        if (!value) {
            setData((prev) => ({...prev, request_id: "", amount: ""}));
            return;
        }

        setData((prev) => ({...prev, request_id: value?.request_id || ""}));

        // Auto-fill amount if request has a price
        if (value?.price) {
            setData((prev) => ({...prev, amount: value.price.toString()}));
        }
    };

    const handleUserChange = (event, value) => {
        setSelectedUser(value);
        setData((prev) => ({
            ...prev,
            user_id: value?.id || "",
            request_id: "", // Reset request selection when user changes
            amount: "", // Reset amount when user changes
        }));
    };

    // Helper function to get request display label
    const getRequestLabel = (request) => {
        if (!request) return "";
        return `#${request.request_id} - ${request.our_service || 'Service'} - $${request.price || '0.00'}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const requiredFields = [
            "amount",
            "user_id",
            "request_id",
        ];

        const missingFields = requiredFields.filter(
            (field) => !data[field]
        );

        if (missingFields.length > 0) {
            errorToaster(
                `Please fill in all required fields: ${missingFields.join(", ")}`
            );
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        const form_data = new FormData();
        // Append form data fields
        Object.keys(data).forEach((key) => {
            const value = data[key];
            if (value) {
                form_data.append(key, value);
            }
        });

        // Add the note separately
        if (note) {
            form_data.append("note", note);
        }

        const result = await Helper.Post({
            url: api_Routes.OrderRequests.Add,
            data: form_data,
            hasToken: true,
        });

        const {response, message} = result || {};

        if (response) {
            successToaster(message);
            navigate("/OrderRequests");
        }
        setIsLoading(false);
    };

    return (
        <Styles>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-6">
                <div className="flex flex-col justify-between items-center gap-6 md:flex-row w-full">
                    {/* Requests Autocomplete */}
                    <div className="w-full">
                        <Autocomplete
                            disablePortal
                            loading={isRequestsLoading}
                            options={userFilteredRequests}
                            getOptionLabel={getRequestLabel}
                            onChange={handleRequestChange}
                            value={selectedRequest}
                            disabled={!data.user_id}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Request"
                                    variant="outlined"
                                    helperText={!data.user_id ? "Please select a user first" : ""}
                                />
                            )}
                        />
                    </div>

                    {/* Users Autocomplete */}
                    <div className="w-full">
                        <Autocomplete
                            disablePortal
                            loading={isUsersLoading}
                            options={users}
                            getOptionLabel={(option) => option.name || option.username || ""}
                            onChange={handleUserChange}
                            renderInput={(params) => (
                                <TextField {...params} label="User" variant="outlined"/>
                            )}
                        />
                    </div>
                </div>

                <div className="flex flex-col justify-between items-center gap-x-6 md:flex-row">
                    <TextField
                        fullWidth
                        label="Amount"
                        name="amount"
                        type={'number'}
                        required
                        autoComplete="off"
                        value={data.amount}
                        onChange={handleInputChange}
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                    <TextField
                        fullWidth
                        label="Note"
                        name="note"
                        multiline
                        rows={3}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                </div>

                <div className="submit-btn">
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <CircularProgress size={22} sx={{color: "white"}}/>
                        ) : (
                            "Submit"
                        )}
                    </Button>
                </div>
            </form>
        </Styles>
    );
};

export default PanelAddOrderRequest;